import { Activity, ChevronDown, ChevronRight, RefreshCw, Smartphone, Store } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { eventsService } from '../services/api';
import './AuditLog.css';
import './Events.css';

const LIMIT = 50;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function PayloadCell({ payload }) {
  const [open, setOpen] = useState(false);
  if (!payload || Object.keys(payload).length === 0) return <span className="al-meta-empty">—</span>;
  return (
    <div className="al-meta-wrap">
      <button className="al-meta-toggle" onClick={() => setOpen(v => !v)}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>{open ? 'Nascondi' : 'Mostra'}</span>
      </button>
      {open && <pre className="al-meta-pre">{JSON.stringify(payload, null, 2)}</pre>}
    </div>
  );
}

function EventTypeBadge({ type }) {
  const isError = /error|fail|refund/i.test(type);
  const isWarn = /fallback|warn|timeout/i.test(type);
  const isSuccess = /success|applied|connected|activated/i.test(type);
  const cls = isError ? 'ev-badge-danger' : isWarn ? 'ev-badge-warn' : isSuccess ? 'ev-badge-success' : 'ev-badge-default';
  return <span className={`ev-badge ${cls}`}>{type}</span>;
}

// ─── VenueEvents tab ───────────────────────────────────────────
function VenueEventsTab() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [filters, setFilters] = useState({ eventType: '', q: '', from: '', to: '' });

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await eventsService.getVenueEvents({ ...filters, page: p, limit: LIMIT });
      setEvents(res.events || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    eventsService.getVenueEventTypes().then(r => setTypes(r.types || [])).catch(() => {});
  }, []);

  useEffect(() => { load(1); }, [load]);

  return (
    <div>
      <div className="al-filter-bar">
        <select className="al-select" value={filters.eventType} onChange={e => setFilters(f => ({ ...f, eventType: e.target.value }))}>
          <option value="">Tutti i tipi</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className="al-input" placeholder="Cerca tipo / sorgente / utente…" value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} />
        <input className="al-input" type="date" value={filters.from}
          onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
        <input className="al-input" type="date" value={filters.to}
          onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
        <button className="al-btn al-btn-ghost" onClick={() => load(1)} disabled={loading}>
          <RefreshCw size={14} /> Aggiorna
        </button>
      </div>

      <div className="al-table-wrap">
        <table className="al-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Venue / Tenant</th>
              <th>Tipo evento</th>
              <th>Sorgente</th>
              <th>Identificatore</th>
              <th>Utente</th>
              <th>Payload</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="al-empty">Caricamento…</td></tr>
            )}
            {!loading && events.length === 0 && (
              <tr><td colSpan={7} className="al-empty">Nessun evento trovato.</td></tr>
            )}
            {!loading && events.map(ev => (
              <tr key={ev.id}>
                <td className="al-nowrap">{formatDate(ev.createdAt)}</td>
                <td>
                  <span className="ev-name">{ev.venue?.name || ev.venueId}</span>
                  {ev.venue?.tenant && <span className="ev-sub">{ev.venue.tenant.company_name}</span>}
                </td>
                <td><EventTypeBadge type={ev.eventType} /></td>
                <td>{ev.sourceType || '—'}</td>
                <td className="al-mono">{ev.sourceIdentifier || '—'}</td>
                <td>{ev.byUser || '—'}</td>
                <td><PayloadCell payload={ev.payload} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="al-pagination">
        <span className="al-count">{total} eventi totali</span>
        <div className="al-page-btns">
          <button className="al-btn al-btn-ghost" disabled={page <= 1} onClick={() => load(page - 1)}>‹ Prec</button>
          <span className="al-page-info">Pag. {page} / {totalPages}</span>
          <button className="al-btn al-btn-ghost" disabled={page >= totalPages} onClick={() => load(page + 1)}>Succ ›</button>
        </div>
      </div>
    </div>
  );
}

// ─── DeviceEvents tab ──────────────────────────────────────────
function DeviceEventsTab() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [filters, setFilters] = useState({ eventType: '', q: '', from: '', to: '' });

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await eventsService.getDeviceEvents({ ...filters, page: p, limit: LIMIT });
      setEvents(res.events || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    eventsService.getDeviceEventTypes().then(r => setTypes(r.types || [])).catch(() => {});
  }, []);

  useEffect(() => { load(1); }, [load]);

  return (
    <div>
      <div className="al-filter-bar">
        <select className="al-select" value={filters.eventType} onChange={e => setFilters(f => ({ ...f, eventType: e.target.value }))}>
          <option value="">Tutti i tipi</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className="al-input" placeholder="Cerca tipo / utente…" value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} />
        <input className="al-input" type="date" value={filters.from}
          onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
        <input className="al-input" type="date" value={filters.to}
          onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
        <button className="al-btn al-btn-ghost" onClick={() => load(1)} disabled={loading}>
          <RefreshCw size={14} /> Aggiorna
        </button>
      </div>

      <div className="al-table-wrap">
        <table className="al-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Device / Venue</th>
              <th>Tipo evento</th>
              <th>Utente</th>
              <th>Payload</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="al-empty">Caricamento…</td></tr>
            )}
            {!loading && events.length === 0 && (
              <tr><td colSpan={5} className="al-empty">Nessun evento trovato.</td></tr>
            )}
            {!loading && events.map(ev => (
              <tr key={ev.id}>
                <td className="al-nowrap">{formatDate(ev.createdAt)}</td>
                <td>
                  <span className="ev-name">{ev.device?.label || ev.deviceId}</span>
                  <span className="ev-sub al-mono">{ev.device?.setupCode}</span>
                  {ev.device?.venue && <span className="ev-sub">{ev.device.venue.name}</span>}
                </td>
                <td><EventTypeBadge type={ev.eventType} /></td>
                <td>{ev.byUser || '—'}</td>
                <td><PayloadCell payload={ev.payload} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="al-pagination">
        <span className="al-count">{total} eventi totali</span>
        <div className="al-page-btns">
          <button className="al-btn al-btn-ghost" disabled={page <= 1} onClick={() => load(page - 1)}>‹ Prec</button>
          <span className="al-page-info">Pag. {page} / {totalPages}</span>
          <button className="al-btn al-btn-ghost" disabled={page >= totalPages} onClick={() => load(page + 1)}>Succ ›</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────
export default function Events() {
  const [tab, setTab] = useState('venue');

  return (
    <div className="al-page">
      <div className="al-header">
        <div className="al-header-left">
          <Activity size={22} className="al-header-icon" />
          <h1>Eventi</h1>
        </div>
      </div>

      <div className="ev-tabs">
        <button className={`ev-tab ${tab === 'venue' ? 'ev-tab-active' : ''}`} onClick={() => setTab('venue')}>
          <Store size={15} /> Venue Events
        </button>
        <button className={`ev-tab ${tab === 'device' ? 'ev-tab-active' : ''}`} onClick={() => setTab('device')}>
          <Smartphone size={15} /> Device Events
        </button>
      </div>

      {tab === 'venue' && <VenueEventsTab />}
      {tab === 'device' && <DeviceEventsTab />}
    </div>
  );
}
